import { useTranslation } from 'react-i18next';
import { ROUTINGS, SHAPES, type SceneStore } from '@os/doc';
import {
  ArrowDown,
  ArrowUp,
  BringToFront,
  Copy,
  LayoutGrid,
  Lock,
  LockOpen,
  SendToBack,
  Shrink,
  Trash2,
} from 'lucide-react';
import type { EditorController } from './controller';
import { authorIds, authorsOf, type AuthorsSource } from './authors';
import { useAuthorNames } from './useAuthors';
import type { SelectionProps } from './useSelectionProps';
import type { ObjectData } from '@os/doc';
import { OBJECT_COLORS, swatchOf } from '../../ui/palette';
import { SECTION_COLORS, sectionColorOf } from '../../ui/section-colors';
import { Checkbox, ColorSwatch, IconButton, Input, Select, Switch } from '../../ui';
import { Icon } from '../../ui/icons';

/** The selection's controls, shared by the phone's properties sheet and the context bar (P5f). */

const ORDER_ICONS = {
  front: BringToFront,
  forward: ArrowUp,
  backward: ArrowDown,
  back: SendToBack,
} as const;

export const COLORS = OBJECT_COLORS;
const FONT_SIZES = [12, 16, 24, 32, 48, 64, 96];

type FieldProps = { p: SelectionProps; store: SceneStore };

/** "In: section name" when the whole selection sits in one section. */
export function HomeLine({ p }: { p: SelectionProps }) {
  const { t } = useTranslation();
  const home = p.home;
  if (home?.type !== 'section') return null;
  return <p className="truncate text-xs text-fg-muted">{t('panel.inSection', { name: home.name.trim() || t('editor.untitledSection') })}</p>;
}

/** Whether the selection has a colour to pick (images have none). */
export const hasColor = (p: SelectionProps) => p.objects.some((o) => o.type !== 'image');

/** Object colours, or the section pairs when every selected object is a section (P5e). */
export function ColorField({ p, store }: FieldProps) {
  const { t } = useTranslation();
  const { sections, objects, ids, current, setColor } = p;
  return (
    <fieldset>
      <legend className="mb-1.5 text-xs font-medium text-fg-muted">{t('panel.color')}</legend>
      {sections.length === objects.length ? (
        // Sections: a light fill and its pill colour, as pairs (P5e).
        <div className="grid grid-cols-8 gap-1.5 pointer-coarse:grid-cols-6">
          {SECTION_COLORS.map((c) => (
            <ColorSwatch
              key={c.id}
              data-section-color={c.id}
              color={c.fill}
              edge={c.accent}
              label={t(`colors.${c.id}`)}
              pressed={typeof current === 'string' && sectionColorOf(current) === c.id}
              onClick={() => store.commands.setProps(ids, { fill: c.fill })}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-6 gap-1.5">
          {COLORS.map((c) => (
            <ColorSwatch key={c.id} color={c.hex} label={t(`colors.${c.id}`)} pressed={typeof current === 'string' && swatchOf(current) === c.id} onClick={() => setColor(c.hex)} />
          ))}
        </div>
      )}
    </fieldset>
  );
}

/** Font size, shape kind, section name and options, connector label, routing and arrows: what the type has. */
export function TypeFields({ p, store }: FieldProps) {
  const { t } = useTranslation();
  const { texts, shapes, sections, connectors, ids } = p;
  const lists = p.objects.every((o) => o.type === 'tasklist') ? (p.objects as Extract<ObjectData, { type: 'tasklist' }>[]) : [];
  return (
    <>
      {lists.length > 0 && (
        <Switch label={t('tasks.compactMode')} checked={lists.every((o) => o.compact)} onCheckedChange={(v) => store.commands.setProps(ids, { compact: v })} className="text-xs text-fg-muted" />
      )}
      {texts.length > 0 && (
        <label className="flex items-center justify-between gap-2 text-xs text-fg-muted">
          {t('panel.fontSize')}
          <Select
            aria-label={t('panel.fontSize')}
            value={texts[0]!.type === 'text' ? texts[0]!.fontSize : 24}
            onChange={(e) =>
              store.commands.setProps(
                texts.map((o) => o.id),
                { fontSize: Number(e.target.value) },
              )
            }
            className="h-8 w-auto px-2"
          >
            {FONT_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </label>
      )}
      {shapes.length > 0 && (
        <label className="flex items-center justify-between gap-2 text-xs text-fg-muted">
          {t('panel.shape')}
          <Select
            aria-label={t('panel.shape')}
            value={shapes[0]!.type === 'shape' ? shapes[0]!.shape : 'rectangle'}
            onChange={(e) =>
              store.commands.setProps(
                shapes.map((o) => o.id),
                { shape: e.target.value as (typeof SHAPES)[number] },
              )
            }
            className="h-8 w-auto px-2"
          >
            {SHAPES.map((s) => (
              <option key={s} value={s}>
                {t(`shapes.${s}`)}
              </option>
            ))}
          </Select>
        </label>
      )}
      {sections.length === 1 && (
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          {t('editor.sectionName')}
          <Input
            key={`${sections[0]!.id}:${sections[0]!.name}`}
            type="text"
            defaultValue={sections[0]!.name}
            maxLength={200}
            onBlur={(e) =>
              e.currentTarget.value !== sections[0]!.name &&
              store.commands.setProps(ids, { name: e.currentTarget.value })
            }
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className="h-8 px-2"
          />
        </label>
      )}
      {sections.length > 0 && (
        <div className="flex flex-col gap-1 text-xs text-fg-muted">
          <Checkbox label={t('panel.clip')} checked={sections.every((o) => o.clip)} onCheckedChange={(v) => store.commands.setProps(ids, { clip: v })} className="text-xs text-fg-muted" />
          <Checkbox label={t('panel.collapsed')} checked={sections.every((o) => o.collapsed)} onCheckedChange={(v) => store.commands.setProps(ids, { collapsed: v })} className="text-xs text-fg-muted" />
        </div>
      )}
      {connectors.length === 1 && (
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          {t('editor.connectorLabel')}
          <Input
            key={`${connectors[0]!.id}:${connectors[0]!.label ?? ''}`}
            type="text"
            defaultValue={connectors[0]!.label ?? ''}
            maxLength={200}
            onBlur={(e) =>
              e.currentTarget.value !== (connectors[0]!.label ?? '') &&
              store.commands.setProps(ids, { label: e.currentTarget.value })
            }
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className="h-8 px-2"
          />
        </label>
      )}
      {connectors.length > 0 && (
        <div className="flex flex-col gap-1 text-xs text-fg-muted">
          <label className="flex items-center justify-between gap-2">
            {t('panel.routing')}
            <Select
              aria-label={t('panel.routing')}
              value={connectors[0]!.routing}
              onChange={(e) =>
                store.commands.setProps(ids, {
                  routing: e.target.value as (typeof ROUTINGS)[number],
                })
              }
              className="h-8 w-auto px-2"
            >
              {ROUTINGS.map((r) => (
                <option key={r} value={r}>
                  {t(`panel.routings.${r}`)}
                </option>
              ))}
            </Select>
          </label>
          <Checkbox label={t('panel.startArrow')} checked={connectors.every((o) => o.startCap === 'arrow')} onCheckedChange={(v) => store.commands.setProps(ids, { startCap: v ? 'arrow' : 'none' })} className="text-xs text-fg-muted" />
          <Checkbox label={t('panel.endArrow')} checked={connectors.every((o) => o.endCap === 'arrow')} onCheckedChange={(v) => store.commands.setProps(ids, { endCap: v ? 'arrow' : 'none' })} className="text-xs text-fg-muted" />
        </div>
      )}
    </>
  );
}

/** Bring to front, forward, backward, send to back. */
export function OrderField({ p, store }: FieldProps) {
  const { t } = useTranslation();
  const { ids } = p;
  return (
    <fieldset>
      <legend className="mb-1 text-xs font-medium text-fg-muted">{t('panel.order')}</legend>
      <div className="flex gap-0.5">
        {(['front', 'forward', 'backward', 'back'] as const).map((where) => (
          <IconButton
            key={where}
            size="sm"
            label={t(`panel.${where}`)}
            icon={<Icon icon={ORDER_ICONS[where]} size={16} />}
            onClick={() => store.commands.reorder(ids, where)}
          />
        ))}
      </div>
    </fieldset>
  );
}

/** Lock, duplicate, arrange, fit to content (one section), delete: `className` styles the row. */
export function ActionButtons({ p, store, controller, className }: FieldProps & { controller: EditorController; className?: string }) {
  const { t } = useTranslation();
  const { ids, sections, objects, allLocked, inherited } = p;
  return (
    <div className={className ?? 'flex gap-0.5 border-t border-border pt-2'}>
      <IconButton
        size="sm"
        label={t(allLocked ? 'panel.unlock' : 'panel.lock')}
        pressed={allLocked}
        disabled={inherited}
        icon={<Icon icon={allLocked ? Lock : LockOpen} size={16} />}
        onClick={() => store.commands.setProps(ids, { locked: !allLocked })}
      />
      <IconButton
        size="sm"
        label={t('commands.duplicate')}
        icon={<Icon icon={Copy} size={16} />}
        onClick={() => controller.runCommand('duplicate')}
      />
      {controller.canArrange(ids) && (
        <IconButton
          size="sm"
          label={t('commands.arrange')}
          icon={<Icon icon={LayoutGrid} size={16} />}
          onClick={() => controller.runCommand('arrange')}
        />
      )}
      {sections.length === 1 && objects.length === 1 && !allLocked && !store.readOnly && (
        <IconButton
          size="sm"
          label={t('commands.fitToContent')}
          icon={<Icon icon={Shrink} size={16} />}
          onClick={() => controller.runCommand('fitToContent')}
        />
      )}
      <IconButton
        size="sm"
        label={t('commands.delete')}
        disabled={allLocked}
        icon={<Icon icon={Trash2} size={16} />}
        onClick={() => controller.runCommand('delete')}
        className="ml-auto hover:text-danger"
      />
    </div>
  );
}

/** "Added by Ada · 2 hours ago", "3 authors"; nothing while the names load. */
export function AuthorLine({
  source,
  objects,
}: {
  source: AuthorsSource;
  objects: readonly ObjectData[];
}) {
  const { t, i18n } = useTranslation();
  const names = useAuthorNames(source, authorIds(objects));
  const view = authorsOf(objects, { me: source.me, names, t, lng: i18n.language });
  if (!view) return null;
  const who =
    view.kind === 'person'
      ? t('authors.addedBy', { name: view.label })
      : view.kind === 'you'
        ? t('authors.addedByYou')
        : view.label;
  return (
    <p data-author-line="" className="flex items-center gap-1.5 truncate text-xs text-fg-muted">
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full bg-fg-subtle"
        style={view.color ? { backgroundColor: view.color } : undefined}
      />
      <span className="truncate">{view.when ? `${who} · ${view.when}` : who}</span>
    </p>
  );
}
